/**
 * The browser entry point.
 *
 * Fonts are self-hosted through `@fontsource` — the same packages the console
 * uses, for the same reason: no third-party origin, so the page works behind a
 * strict CSP and on a machine with no route to a font CDN.
 *
 * **Both `latin` and `latin-ext` are imported, and that is not optional.** The
 * `latin` subset has no `ğ ş ı İ`; importing it alone drops every one of them to
 * a fallback face, silently, in a Turkish-first page. It looks like a rendering
 * bug and is a missing import.
 */
import '@fontsource/ibm-plex-sans/latin-400.css'
import '@fontsource/ibm-plex-sans/latin-500.css'
import '@fontsource/ibm-plex-sans/latin-600.css'
import '@fontsource/ibm-plex-sans/latin-ext-400.css'
import '@fontsource/ibm-plex-sans/latin-ext-500.css'
import '@fontsource/ibm-plex-sans/latin-ext-600.css'
import '@fontsource/ibm-plex-mono/latin-400.css'
import '@fontsource/ibm-plex-mono/latin-500.css'
import '@fontsource/ibm-plex-mono/latin-ext-400.css'
import '@fontsource/ibm-plex-mono/latin-ext-500.css'
import '@fontsource/chakra-petch/latin-500.css'
import '@fontsource/chakra-petch/latin-600.css'
import '@fontsource/chakra-petch/latin-ext-500.css'
import '@fontsource/chakra-petch/latin-ext-600.css'

// The one thing taken from the console: the palette, which is a documented
// WCAG contract rather than a set of colours. See vite.config.ts.
import '@tokens/tokens.css'
import './design-system/landing-tokens.css'
import './design-system/base.css'
import './design-system/scrollbars.css'

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import { App } from './App'
import { isLocale, type Locale } from './i18n'
import { isPage, pageFromPath, type PageId } from './routes'

/**
 * The locale and the page are read from what the prerender stamped on `<html>`,
 * never re-derived from `location.pathname`. One source at runtime: two would
 * eventually disagree, and a disagreement here is a hydration mismatch that
 * shows up on exactly one URL shape.
 *
 * `pageFromPath` exists for the same reason a fallback locale does — a
 * hand-built document, a dev server, a file opened off disk — and is asked only
 * once the stamp has been found missing.
 */
const locale: Locale = isLocale(document.documentElement.lang)
  ? (document.documentElement.lang as Locale)
  : 'tr'

const stamped = document.documentElement.dataset.page
const page: PageId = isPage(stamped)
  ? stamped
  : (pageFromPath(window.location.pathname, locale) ?? 'home')

const root = document.getElementById('root')
if (!root) throw new Error('#root is missing from index.html')

createRoot(root).render(
  <StrictMode>
    <App locale={locale} page={page} />
  </StrictMode>,
)
